import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import axios from "axios";

import FooterModal from "../common/FooterModal";
import CircleInfoIcon from "../icons/heroicons/outline/CircleInfoIcon";
import FormItem from "../common/FormItem";
import Button from "../common/Button";
import DangerButton from "../common/DangerButton";

import { useLiffProfile } from "../../context/LiffProfileContext";

const SelectStoreLocationModal = ({
  show,
  couponDetail,
  onOk,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();
  const [locations, setLocations] = useState([]);
  const [province, setProvince] = useState(null);
  const [district, setDistrict] = useState(null);
  const [location, setLocation] = useState(null);
  const [selectedLocationDetail, setSelectedLocationDetail] = useState(null);
  const seniorLayout = localStorage.getItem("seniorLayout") === "true";

  useEffect(() => {
    if (show && couponDetail?.participants?.length) {
      loadLocations();
    }
  }, [show]);

  useEffect(() => {
    if (location?.id) {
      loadLocationDetail(location.id);
    }
  }, [location]);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
      "x-user-id": userId,
      "Accept-Language": i18n.language.toUpperCase(),
    }),
    [authToken, userId, i18n.language]
  );

  const getPartnersLocations = async (partnersLocations) => {
    try {
      const query = new URLSearchParams(partnersLocations).toString();
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/clm-partners/locations?${query}`;
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      console.error("Error fetching point:", error);
    }
  };

  const getPartnerLocationDetail = async (id) => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/clm-partners/locations/${id}`;
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      console.error("Error fetching point:", error);
    }
  };

  const loadLocations = async () => {
    try {
      const res = await getPartnersLocations({
        partnerCodes: couponDetail.participants,
      });
      if (res.statusCode === 200) {
        setLocations(res.data.items);
      }
    } catch (err) {
      console.error("loadLocations failed", err);
    }
  };

  const loadLocationDetail = async (id) => {
    try {
      const res = await getPartnerLocationDetail(id);
      if (res.statusCode === 200) {
        setSelectedLocationDetail(res.data);
      }
    } catch (err) {
      console.error("loadLocationDetail failed", err);
    }
  };

  const resetForm = () => {
    setProvince(null);
    setDistrict(null);
    setLocation(null);
    setSelectedLocationDetail(null);
  };

  const handleBack = () => {
    resetForm();
    onCancel();
  };

  const handleConfirm = () => {
    if (selectedLocationDetail?.code) {
      onOk(selectedLocationDetail.code);
      resetForm();
    }
  };

  const provinces = useMemo(() => {
    return [...new Set(locations.map((loc) => loc.address.city))];
  }, [locations]);

  const filteredDistricts = useMemo(() => {
    return [
      ...new Set(
        locations
          .filter((loc) => !province || loc.address.city === province.value)
          .map((loc) => loc.address.street)
      ),
    ];
  }, [province, locations]);

  const filteredLocations = useMemo(() => {
    return locations.filter(
      (loc) =>
        (!province || loc.address.city === province.value) &&
        (!district || loc.address.street === district.value)
    );
  }, [province, district, locations]);

  const pickUpDate = moment().add(14, "days").format("LL");

  return (
    <FooterModal show={show}>
      <div className="w-full px-10 xs:px-6 pt-7 pb-10 flex flex-col items-center gap-y-2">
        <div className="w-full text-2xl font-semibold pb-2">
          {t("pick_up_premium_location")}
        </div>

        <div className="w-full flex flex-col gap-y-4">
          <FormItem label={t("province")}>
            <Select
              value={province}
              onChange={(val) => {
                setProvince(val);
                setDistrict(null);
                setLocation(null);
              }}
              options={provinces.map((p) => ({ label: p, value: p }))}
              placeholder={t("data_not_found")}
            />
          </FormItem>

          <FormItem label={t("district")}>
            <Select
              value={district}
              onChange={(val) => {
                setDistrict(val);
                setLocation(null);
              }}
              options={filteredDistricts.map((d) => ({
                label: d,
                value: d,
              }))}
              placeholder={t("data_not_found")}
            />
          </FormItem>

          <FormItem label={t("branch_name")}>
            <Select
              value={location}
              onChange={(val) => setLocation(val)}
              getOptionLabel={(opt) => opt.name}
              getOptionValue={(opt) => opt.id}
              options={filteredLocations}
              placeholder={t("data_not_found")}
            />
          </FormItem>

          {selectedLocationDetail && (
            <div className="w-full mt-10 mb-2">
              <div className="w-full flex gap-x-4 p-4 rounded-lg border border-secondary-border">
                <CircleInfoIcon className="w-8 h-8 flex-shrink-0" />
                <div>
                  <div>
                    <span className="text-lg font-semibold">
                      {t("pick_up_date")} : {pickUpDate}
                    </span>
                  </div>
                  <div className="mt-2 text-secondary-text-note text-base">
                    {t("branch")} {selectedLocationDetail.name} {t("address")}{" "}
                    {selectedLocationDetail.description}
                  </div>
                </div>
              </div>
              <div className="mt-1 text-[1.3rem] font-semibold leading-8 text-center">
                {t("save_screenshot_before_confirming")}
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="w-full flex gap-x-3 mt-6">
          <Button
            type="button"
            className={`w-full h-13 font-medium ${
              seniorLayout ? "text-xl" : "text-lg"
            }`}
            shape="round"
            onClick={handleBack}
          >
            {t("go_back")}
          </Button>
          <div className="w-full h-13">
            <DangerButton
              buttonName={t("confirm")}
              seniorLayout={seniorLayout}
              onClick={handleConfirm}
            />
          </div>
        </div>
      </div>
    </FooterModal>
  );
};

export default SelectStoreLocationModal;
